/**
 * Admin 공통 폼: Form.ts 활용 (async, await, try/catch, submitting, disabled, loader)
 * data-admin-ajax="1" 인 폼에 자동 바인딩. 제출 시 fetch + JSON 응답 처리, redirect 또는 메시지.
 * admin-auth__form 은 AdminAuthController에서 처리하므로 제외.
 */

import { Form } from "../../core/Form";
import { Modal } from "../../utils/Modal";
import { Toast } from "../../utils/Toast";
import { NumberStepper } from "../../components/NumberStepper";
import { QuillEditor } from "../../components/QuillEditor";

const SELECTOR = 'form[data-admin-ajax="1"]';
const LOADER_SELECTOR = ".admin-form__loader";

function getRedirect(data: Record<string, unknown>): string | undefined {
  const r = data?.redirect ?? (data?.data as Record<string, unknown> | undefined)?.redirect;
  return typeof r === "string" ? r : undefined;
}

function getMessage(data: Record<string, unknown>): string {
  return (data?.msg ?? data?.message ?? "") as string;
}

/**
 * 단일 form 에 admin AJAX 바인딩. 이미 바인딩된 form (data-admin-ajax-bound="1") 은 스킵.
 * 정적/동적 모두 이 함수 통해 안전하게 초기화 가능.
 */
export function bindAdminForm(form: HTMLFormElement): void {
  if (form.dataset.adminAjaxBound === "1") return;
  // admin-auth__form 은 기본적으로 AdminAuthController(로그인/회원가입)가 전담하므로 제외.
  // 단, data-admin-ajax="1" 로 명시 옵트인한 폼(예: 임시 비밀번호 발급)은 이 generic 바인더가 처리.
  if (form.classList.contains("admin-auth__form") && form.dataset.adminAjax !== "1") return;
  const url = form.action || form.dataset.submitUrl || "";
  if (!url) return;
  form.dataset.adminAjaxBound = "1";

  const confirmMsg = form.dataset.confirm;
  const f = new Form(form, {
    submitUrl: url,
    loaderSelector: LOADER_SELECTOR,
    validate:
      confirmMsg != null
        ? async () => (await Modal.confirm(confirmMsg)) ? null : ""
        : undefined,
    requestFailedMessage: "요청에 실패했습니다.",
    invalidResponseMessage: "응답을 처리할 수 없습니다.",
    onSuccess(data) {
      const action = form.dataset.successAction ?? "";
      const redirect = getRedirect(data);
      const msg = getMessage(data);

      // success-action 분기는 redirect 처리보다 먼저 (UI 동기화 후 redirect 가 필요하면 분기 내부에서 호출)
      if (action === "remove-row") {
        const row = form.closest("tr");
        if (row) {
          const rowId = row.dataset.rowId ?? row.dataset.id ?? "";
          if (rowId) {
            const wrap = row.closest<HTMLElement>("[data-listing-table], [data-table-manager]");
            wrap?.querySelector<HTMLElement>(`.admin-table-card[data-row-id="${rowId}"]`)?.remove();
          }
          const tbody = row.parentElement;
          row.remove();
          if (tbody && !tbody.querySelector("tr")) {
            const wrap = tbody.closest<HTMLElement>("[data-listing-table], [data-table-manager]");
            wrap?.classList.add("is-empty");
            const emptyState = wrap?.querySelector<HTMLElement>("[data-empty-state]");
            if (emptyState) emptyState.hidden = false;
          }
        }
        if (msg) Toast.success(msg);
        return;
      }

      if (action === "notification-mark-read") {
        // 해당 알림 아이템에서 unread 상태 제거 + 폼 제거 + 카운트 갱신 이벤트 발행
        const item = form.closest<HTMLElement>(
          ".admin-header__notifications-item, .admin-notifications__item",
        );
        item?.classList.remove(
          "is-unread",
          "admin-notifications__item--unread",
        );
        item?.querySelector<HTMLElement>(".admin-notifications__unread-dot")?.remove();
        form.remove();
        dispatchUnreadChanged(data);
        if (msg) Toast.success(msg);
        return;
      }

      if (action === "notification-mark-all-read") {
        document
          .querySelectorAll<HTMLElement>(
            ".admin-header__notifications-item.is-unread, .admin-notifications__item--unread",
          )
          .forEach((el) => {
            el.classList.remove("is-unread", "admin-notifications__item--unread");
          });
        document
          .querySelectorAll<HTMLElement>(
            ".admin-header__notifications-item-mark, .admin-notifications__mark-form, .admin-notifications__unread-dot",
          )
          .forEach((el) => el.remove());
        // 페이지 헤더에 있던 '모두 읽음' 자기 자신도 제거 (totalUnread=0 이므로 의미 없음)
        form.remove();
        dispatchUnreadChanged(data);
        if (msg) Toast.success(msg);
        return;
      }

      if (action === "temp-password") {
        // 임시 비밀번호 발급: 응답 값(data.data.temp_password)을 모달로 노출(복사 가능한 텍스트).
        // 값이 없으면(이메일 미존재 프라이버시 응답) 메시지만 표시. redirect 하지 않음(모달 유지).
        const payload = (data?.data as Record<string, unknown> | undefined) ?? {};
        const tempPw = typeof payload.temp_password === "string" ? payload.temp_password : "";
        if (tempPw) {
          void Modal.alert(
            `${msg}\n\n임시 비밀번호: ${tempPw}`.trim(),
            "임시 비밀번호 발급",
          );
        } else if (msg) {
          void Modal.alert(msg, "임시 비밀번호 발급");
        }
        return;
      }

      if (redirect) {
        window.location.href = redirect;
        return;
      }
      if (msg) void Modal.alert(msg);
    },
    onFail(msg) {
      if (msg) void Modal.alert(msg); // 빈 문자열(confirm 취소)은 알림 생략
    },
  });
  f.init();
}

/**
 * data-admin-ajax="1" 폼들을 scope 안에서 모두 바인딩. 이미 바인딩된 폼은 스킵.
 * 동적으로 새 폼이 DOM 에 추가됐을 때 호출.
 */
export function bindAdminForms(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLFormElement>(SELECTOR).forEach((form) => {
    bindAdminForm(form);
  });
}

/** Mark-read/Mark-all-read 후 카운트 동기화 이벤트 발행. */
function dispatchUnreadChanged(data: Record<string, unknown>): void {
  const payload = ((data?.data as Record<string, unknown> | undefined) ?? data) as Record<string, unknown>;
  window.dispatchEvent(
    new CustomEvent("admin:notifications:unread-changed", { detail: payload }),
  );
}

export function initAdminForms(): void {
  bindAdminForms(document);
}

/**
 * admin 영역의 date, datetime-local, time 입력: 인풋 클릭 시 선택기 열기 (showPicker).
 * 브라우저 기본은 우측 아이콘만 클릭 시 열리는 경우가 있어, 인풋 클릭 시 showPicker() 호출로 통일.
 */
export function initAdminDateTimeInputs(): void {
  const root = document.body.classList.contains("admin") ? document.body : document.querySelector(".admin");
  if (!root) return;

  const inputs = root.querySelectorAll<HTMLInputElement>(
    'input[type="date"], input[type="datetime-local"], input[type="time"]',
  );
  inputs.forEach((input) => {
    input.addEventListener("click", () => {
      if (typeof input.showPicker === "function") {
        try {
          input.showPicker();
        } catch {
          // 일부 브라우저/상황에서는 showPicker 미지원
        }
      }
    });
  });
}

import { DatePicker } from "../../components/DatePicker";
import { SelectWrapper } from "../../components/SelectWrapper";

/**
 * admin 영역의 [data-datetime-wrap] / [data-time-wrap] 요소에 Flatpickr 적용.
 * native showPicker() 와 별개로, 커스텀 래퍼 스타일의 날짜/시간 선택기를 초기화.
 */
export function initAdminFlatpickrPickers(): void {
  if (!document.body.classList.contains("admin")) return;

  document.querySelectorAll<HTMLElement>("[data-datetime-wrap]").forEach((wrap) => {
    const input = wrap.querySelector<HTMLElement>("input");
    if (!input) return;
    new DatePicker(input, {
      enableTime: true,
      dateFormat: "Y-m-d H:i",
      time24hr: true,
      allowInput: false,
    }).init();
    wrap.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).tagName !== "INPUT") input.dispatchEvent(new MouseEvent("click", { bubbles: false }));
    });
  });

  document.querySelectorAll<HTMLElement>("[data-time-wrap]").forEach((wrap) => {
    const input = wrap.querySelector<HTMLElement>("input");
    if (!input) return;
    new DatePicker(input, {
      enableTime: true,
      noCalendar: true,
      dateFormat: "H:i",
      time24hr: true,
      allowInput: false,
    }).init();
    wrap.addEventListener("click", (e) => {
      if ((e.target as HTMLElement).tagName !== "INPUT") input.dispatchEvent(new MouseEvent("click", { bubbles: false }));
    });
  });
}

/**
 * admin 영역의 모든 select에 SlimSelect 적용 (SelectWrapper 사용).
 * body.admin 내 select.admin-select, select.admin-form__input, select.admin-auth__input 대상.
 *
 * root 파라미터로 동적 추가된 폼 일부도 init 가능. 이중 init 방지를 위해
 * data-admin-select-inited 마크 사용.
 */
export function initAdminSelects(root: ParentNode = document): void {
  if (!document.body.classList.contains("admin")) return;

  const selects = (root as Element | Document).querySelectorAll<HTMLSelectElement>(
    "select.admin-select:not([data-admin-select-inited]), select.admin-form__input:not([data-admin-select-inited]), select.admin-auth__input:not([data-admin-select-inited])",
  );
  selects.forEach((el) => {
    el.dataset.adminSelectInited = "1";
    const addable = el.hasAttribute("data-addable");
    const searchPosition = el.dataset.searchPosition === "top" ? "top" as const : undefined;
    new SelectWrapper(el, { searchPlaceholder: "검색...", addable, searchPosition }).init();

    if (el.dataset.perPageNav) {
      el.addEventListener("change", () => {
        const u = new URL(location.href);
        u.searchParams.set("per_page", el.value);
        u.searchParams.delete("page");
        location.href = u.toString();
      });
    }
  });
}

/**
 * TagInput — [data-tag-input] 요소에 바인딩.
 * Enter / 쉼표로 태그 추가, × 버튼으로 제거. hidden input 배열로 값 유지.
 */
export function initTagInputs(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-tag-input]:not([data-tag-inited])").forEach(el => {
    el.dataset.tagInited = "1";
    const maxTags   = el.dataset.maxTags  ? parseInt(el.dataset.maxTags,  10) : Infinity;
    const maxLength = el.dataset.maxLength ? parseInt(el.dataset.maxLength, 10) : Infinity;
    const field     = el.querySelector<HTMLInputElement>(".admin-tag-input__field");
    const tagsWrap  = el.querySelector<HTMLElement>(".admin-tag-input__tags");
    if (!field || !tagsWrap) return;

    const hiddenName = (() => {
      const first = tagsWrap.querySelector<HTMLInputElement>("input[type=hidden]");
      if (first) return first.name;
      const id = el.id || "";
      return id ? `${id}[]` : "tags[]";
    })();

    function countTags(): number {
      return tagsWrap!.querySelectorAll(".admin-tag-input__chip").length;
    }

    function addTag(raw: string): void {
      const val = raw.trim();
      if (!val || val.length > maxLength) return;
      if (countTags() >= maxTags) return;
      // 중복 방지
      const existing = [...tagsWrap!.querySelectorAll<HTMLInputElement>("input[type=hidden]")].map(i => i.value);
      if (existing.includes(val)) return;

      const chip = document.createElement("span");
      chip.className = "admin-tag-input__chip";

      const lbl = document.createElement("span");
      lbl.className = "admin-tag-input__chip-label";
      lbl.textContent = val;

      const rmBtn = document.createElement("button");
      rmBtn.type = "button";
      rmBtn.className = "admin-tag-input__chip-remove";
      rmBtn.setAttribute("aria-label", `${val} 제거`);
      rmBtn.innerHTML = `<svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1 1L7 7M7 1L1 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
      rmBtn.addEventListener("click", () => chip.remove());

      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = hiddenName;
      hidden.value = val;

      chip.append(lbl, rmBtn, hidden);
      tagsWrap!.appendChild(chip);
    }

    field.addEventListener("keydown", e => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        addTag(field.value);
        field.value = "";
      } else if (e.key === "Backspace" && !field.value) {
        tagsWrap!.querySelector(".admin-tag-input__chip:last-child")?.remove();
      }
    });

    el.addEventListener("click", () => field.focus());
  });
}

export function initNumberSteppers(root: ParentNode = document): void {
  NumberStepper.initAll(root);
}

/**
 * AdminSearchBars — [data-admin-search-bar] 요소에 바인딩.
 * clear 버튼(data-admin-search-clear)을 JS로 제어.
 * - 초기값 있으면 hidden 제거, 없으면 hidden 유지
 * - input 이벤트마다 value 유무로 hidden 토글
 * - clear 클릭 시 value 비우고 input focus 복원
 */
export function initAdminSearchBars(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-admin-search-bar]:not([data-search-bar-inited])").forEach(wrap => {
    wrap.dataset.searchBarInited = "1";
    const input = wrap.querySelector<HTMLInputElement>(".admin-search-bar__input");
    const clearBtn = wrap.querySelector<HTMLButtonElement>("[data-admin-search-clear]");
    if (!input || !clearBtn) return;

    function syncClear(): void {
      if (input!.value !== "") {
        clearBtn!.removeAttribute("hidden");
      } else {
        clearBtn!.setAttribute("hidden", "");
      }
    }

    input.addEventListener("input", syncClear);
    clearBtn.addEventListener("click", () => {
      input.value = "";
      syncClear();
      input.focus();
    });

    syncClear();
  });
}

/**
 * CharCounter — [data-char-counter] 요소에 바인딩.
 * textarea 입력마다 글자 수 갱신. 80% 이상 warn, 100% limit 클래스 추가.
 */
export function initCharCounters(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-char-counter]:not([data-counter-inited])").forEach(wrap => {
    wrap.dataset.counterInited = "1";
    const textarea = wrap.querySelector<HTMLTextAreaElement>("textarea");
    if (!textarea) return;
    const maxlength = parseInt(wrap.dataset.maxlength ?? textarea.maxLength?.toString() ?? "0", 10) || 0;
    if (!maxlength) return;

    // label 영역의 카운터 span — data-char-counter wrap의 형제 .admin-char-counter__header 를 탐색
    const header  = wrap.closest(".admin-form__group, .admin-form__field, .admin-form__block")
                      ?.querySelector<HTMLElement>("[data-char-count]")
                  ?? wrap.previousElementSibling?.querySelector<HTMLElement>("[data-char-count]")
                  ?? null;
    const current = header?.querySelector<HTMLElement>("[data-char-current]") ?? null;

    function refresh(): void {
      const len = textarea!.value.length;
      if (current) current.textContent = String(len);
      const ratio = maxlength > 0 ? len / maxlength : 0;
      current?.classList.toggle("admin-char-counter__current--warn",  ratio >= 0.8 && ratio < 1);
      current?.classList.toggle("admin-char-counter__current--limit", ratio >= 1);
      wrap.classList.toggle("admin-char-counter__wrap--warn",  ratio >= 0.8 && ratio < 1);
      wrap.classList.toggle("admin-char-counter__wrap--limit", ratio >= 1);
    }

    textarea.addEventListener("input", refresh);
    refresh();
  });
}

/**
 * data-quill="1" textarea를 Quill 리치 에디터로 초기화.
 * textarea 앞에 div 컨테이너를 삽입하고 QuillEditor 래퍼로 마운트.
 * 폼 submit 시 에디터 HTML을 textarea 값으로 동기화한다.
 */
export function initAdminQuillEditors(root: ParentNode = document): void {
  root.querySelectorAll<HTMLTextAreaElement>('textarea[data-quill="1"]').forEach((ta) => {
    if ((ta as unknown as { _quillInited?: boolean })._quillInited) return;
    const wrap = document.createElement("div");
    wrap.className = "admin-editor-quill";
    ta.parentNode?.insertBefore(wrap, ta);

    const uploadUrl =
      ta.dataset.uploadUrl ??
      (document.querySelector('meta[name="upload-attachment-url"]') as HTMLMetaElement | null)
        ?.content ?? "";
    const csrf =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)
        ?.content ?? "";

    const editor = new QuillEditor(wrap, {
      initialHtml: ta.value,
      upload: uploadUrl ? { uploadUrl, csrf, fileKey: "image" } : undefined,
    });
    const instance = editor.init();
    if (!instance) {
      wrap.remove();
      return;
    }
    (ta as unknown as { _quillInited?: boolean })._quillInited = true;
    ta.hidden = true;
    instance.root.setAttribute("role", "textbox");
    instance.root.setAttribute("aria-multiline", "true");
    instance.root.setAttribute("aria-label", ta.labels?.[0]?.textContent?.trim() || "본문 내용");
    const imageButton = wrap.previousElementSibling?.querySelector<HTMLButtonElement>("button.ql-image");
    imageButton?.setAttribute("aria-label", "이미지 추가");
    imageButton?.setAttribute("title", "이미지 추가");

    ta.form?.addEventListener(
      "submit",
      () => {
        ta.value = editor.getHtml();
      },
      { capture: true },
    );
  });
}
